import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertTriangle, CheckCircle, ExternalLink, DollarSign } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useStripeConnect } from "@/hooks/useStripeConnect";
import { supabase } from "@/integrations/supabase/client";
import { insertNotificationOnce } from "@/lib/notificationHelpers";

const EXCLUDED_PATHS = ["/auth", "/reset-password"];

/**
 * Global blocking gate.
 * When a lister has ANY incoming booking request on an item they own AND their
 * Stripe payout setup is incomplete, this overlay locks the entire app until
 * they finish onboarding. Without it, they cannot receive money for the rental.
 */
export function PayoutSetupRequiredGate() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const {
    hasAccount,
    onboardingComplete,
    chargesEnabled,
    payoutsEnabled,
    loading: stripeLoading,
    startOnboarding,
    checkStatus,
  } = useStripeConnect();

  const [hasIncomingRequest, setHasIncomingRequest] = useState(false);
  const [firstBookingId, setFirstBookingId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const notifiedRef = useRef(false);

  const isFullyVerified = onboardingComplete && chargesEnabled && payoutsEnabled;
  const onExcludedRoute = EXCLUDED_PATHS.some((p) => location.pathname.startsWith(p));

  // Query incoming requests for items the user owns
  const checkIncoming = useCallback(async () => {
    if (!user) {
      setHasIncomingRequest(false);
      return;
    }
    const today = new Date().toISOString().split("T")[0];
    const { data, error } = await supabase
      .from("bookings")
      .select("id, status, end_date, items!inner(owner_id)")
      .eq("items.owner_id", user.id)
      .in("status", ["pending", "approved"])
      .gte("end_date", today)
      .order("created_at", { ascending: true })
      .limit(1);

    if (!error && data && data.length > 0) {
      setHasIncomingRequest(true);
      setFirstBookingId(data[0].id);
    } else {
      setHasIncomingRequest(false);
      setFirstBookingId(null);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading || !user) return;
    checkIncoming();

    // Realtime: instantly trigger when a new booking lands on user's items
    const channel = supabase
      .channel(`payout-gate-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "bookings" },
        () => {
          checkIncoming();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [authLoading, user, checkIncoming]);

  const shouldBlock =
    !!user &&
    !authLoading &&
    !stripeLoading &&
    !onExcludedRoute &&
    hasIncomingRequest &&
    !isFullyVerified;

  // Send a one-time urgent notification when block first triggers
  useEffect(() => {
    if (shouldBlock && !notifiedRef.current && user) {
      notifiedRef.current = true;
      insertNotificationOnce({
        user_id: user.id,
        type: "payout_setup_urgent",
        title: "⚠️ Action Required: Set up payouts",
        message:
          "Someone wants to rent your item! Complete your payout setup now so you can get paid. You cannot accept, chat, or share pickup codes until this is done.",
        link: "/settings",
        related_booking_id: firstBookingId ?? undefined,
      });
    }
  }, [shouldBlock, user, firstBookingId]);

  const handleStart = async () => {
    setIsStarting(true);
    try {
      const url = await startOnboarding();
      if (url) window.location.href = url;
    } catch (e) {
      // surfaced inline below
    } finally {
      setIsStarting(false);
    }
  };

  const handleCheck = async () => {
    setIsChecking(true);
    try {
      await checkStatus();
    } finally {
      setIsChecking(false);
    }
  };

  if (!shouldBlock) return null;

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="mx-auto mb-4 p-3 rounded-full bg-amber-100">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <DialogTitle className="text-center text-xl">
            Set Up Payouts to Continue
          </DialogTitle>
          <DialogDescription className="text-center">
            Someone wants to rent your item. Before you can chat, accept the
            request, or share a pickup code, you need to set up payouts so we
            can pay you.
          </DialogDescription>
        </DialogHeader>

        <Alert className="border-amber-200 bg-amber-50">
          <AlertDescription className="text-amber-900 text-sm">
            This is a one-time setup that takes about 2 minutes. The rest of
            the app is locked until it's complete.
          </AlertDescription>
        </Alert>

        <div className="space-y-2 py-2">
          <Row done={hasAccount} label="Account created" />
          <Row done={chargesEnabled} label="Can receive payments" />
          <Row done={payoutsEnabled} label="Can receive payouts" />
        </div>

        <div className="flex flex-col gap-2">
          <Button onClick={handleStart} disabled={isStarting} size="lg" className="w-full gap-2">
            {isStarting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
            {hasAccount ? "Continue Payout Setup" : "Start Payout Setup"}
          </Button>
          {hasAccount && (
            <Button variant="outline" onClick={handleCheck} disabled={isChecking} className="w-full">
              {isChecking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <DollarSign className="h-4 w-4 mr-2" />}
              I've Completed Setup
            </Button>
          )}
        </div>

        <p className="text-xs text-center text-muted-foreground mt-2">
          Securely processed by Stripe.
        </p>
      </DialogContent>
    </Dialog>
  );
}

function Row({ done, label }: { done: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {done ? (
        <CheckCircle className="h-4 w-4 text-green-600" />
      ) : (
        <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />
      )}
      <span className={done ? "text-green-700" : "text-muted-foreground"}>{label}</span>
    </div>
  );
}
